import { CompType, GeoCoordinates, GeoSearch, LocationBoundary } from '@aloft/toolkit-shared';
import { SortingState, Table } from '@tanstack/react-table';

import { useEffect, useRef, useState } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import { Feature } from '../../../modules/referenceView/Tkv2MlsMap/types.ts';
import { MAP_SOURCES, MAP_STYLES } from '../../Tkv2Map/consts.ts';
import MapBox from '../../map/MapBox.ts';
import {
  DEFAULT_SELECTED_STATUSES,
  MAP_LAYERS,
  MAP_LAYER_DEFINITIONS,
  MAP_PIN_STATUS_FILTERS,
} from '../../map/constants.ts';
import { Pin, PinStatusFilter } from '../../map/types.ts';
import { useMap } from '../../map/useMap.ts';
import { propertyPinsToGeoJSON } from '../../map/usePinLayerCallbacks.ts';
import { MAP_VIEWS_OVERLAY_BOTTOM_OFFSET_PX } from '../internal/details/MapViewsOverlay.tsx';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';
import { useMunicipalityBoundaryOverlay } from './useMunicipalityBoundaryOverlay.ts';
import { useProximityRequirementCircle } from './useProximityRequirementCircle.ts';
import { useRadiusCircle } from './useRadiusCircle.ts';
import { useRedrawPolygon } from './useRedrawPolygon.ts';

const DEFAULT_ZOOM = 14;
const DEFAULT_GEO_SEARCH: GeoSearch = { t: 'polygon', coords: [] };

const getMapPinStatus = (
  mlsStatus: string | undefined,
  variant: 'sales' | 'rental',
): PinStatusFilter => {
  const status = mlsStatus?.toLowerCase() ?? '';

  if (status.includes('pending')) {
    return MAP_PIN_STATUS_FILTERS.PENDING;
  }
  if (status === 'active' || status.includes('active')) {
    return MAP_PIN_STATUS_FILTERS.ACTIVE;
  }
  if (
    status.includes('sold') ||
    (variant === 'rental' &&
      (status.includes('rent') || status.includes('leased') || status === 'closed'))
  ) {
    return MAP_PIN_STATUS_FILTERS.SOLD;
  }
  return MAP_PIN_STATUS_FILTERS.OTHER;
};

const getMapPinIcon = (status: PinStatusFilter, variant: 'sales' | 'rental'): Pin['icon'] => {
  if (variant === 'rental' && status === MAP_PIN_STATUS_FILTERS.SOLD) {
    return 'rented';
  }

  return status;
};

export const useCompMap = ({
  selectedPropertyId,
  lat,
  lng,
  subjectLat,
  subjectLng,
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  hoveredPropertyId,
  onHoverPropertyChange,
  mapType,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  selectedStatuses: controlledSelectedStatuses,
  onSelectedStatusesChange,
  compSelectionTableVariant = 'sales',
  isFilterPanelOpen,
  geoSearch,
  proximityRequirementMiles,
  onPolygonCreateAndUpdate,
  onPolygonDrawDelete,
  locationBoundary,
  locationBoundaryEnabled,
}: {
  selectedPropertyId: string;
  lat: number;
  lng: number;
  subjectLat: number;
  subjectLng: number;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  sorting: SortingState;
  compSelectionTableRef?: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  setFocusedRow: (rowId: string | undefined) => void;
  hoveredPropertyId?: string;
  onHoverPropertyChange?: (propertyId: string | undefined) => void;
  mapType: string;
  rowSelection?: CompSelectionState;
  isInspectifyOrder?: boolean;
  hasArvOption?: boolean;
  selectedStatuses?: PinStatusFilter[];
  onSelectedStatusesChange?: (statuses: PinStatusFilter[]) => void;
  compSelectionTableVariant?: 'sales' | 'rental';
  isFilterPanelOpen?: boolean;
  geoSearch?: GeoSearch;
  proximityRequirementMiles?: number;
  onPolygonCreateAndUpdate?: (coords: GeoCoordinates) => void;
  onPolygonDrawDelete?: () => void;
  locationBoundary?: LocationBoundary;
  locationBoundaryEnabled?: boolean;
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const pinsRef = useRef<Pin[]>([]);
  const selectedStatusesRef = useRef<PinStatusFilter[]>([]);
  const fitPinsRef = useRef<() => void>(() => {});
  const [mapLoaded, setMapLoaded] = useState(false);

  const [pins, setPins] = useState<Pin[]>([]);
  const [internalSelectedStatuses, setInternalSelectedStatuses] = useState<PinStatusFilter[]>(
    () => [...DEFAULT_SELECTED_STATUSES],
  );

  const selectedStatuses = controlledSelectedStatuses ?? internalSelectedStatuses;
  const setSelectedStatuses = onSelectedStatusesChange ?? setInternalSelectedStatuses;

  useEffect(() => {
    const newPins = data.reduce((acc, property) => {
      if (!property.latitude || !property.longitude) return acc;

      const status = getMapPinStatus(property.mlsStatus, compSelectionTableVariant);

      const selection = rowSelection?.[property.id];
      const isComp =
        selection !== undefined &&
        (!isInspectifyOrder || !hasArvOption ? true : !!selection.aiv || !!selection.arv);

      let valuationType: CompType | undefined;
      if (isInspectifyOrder && hasArvOption && rowSelection && isComp) {
        if (selection?.aiv) {
          valuationType = 'aiv';
        } else if (selection?.arv) {
          valuationType = 'arv';
        }
      }

      acc.push({
        id: property.id,
        lat: property.latitude,
        lng: property.longitude,
        label: property.similarityRank?.toString() ?? '',
        active: isComp,
        selected: property.id === selectedPropertyId,
        hovered: property.id === hoveredPropertyId,
        status: isComp ? MAP_PIN_STATUS_FILTERS['SELECTED COMPS'] : status,
        icon: getMapPinIcon(status, compSelectionTableVariant),
        valuationType,
      });
      return acc;
    }, [] as Pin[]);

    setPins(newPins);
    pinsRef.current = newPins;
  }, [
    data,
    rowSelection,
    isInspectifyOrder,
    hasArvOption,
    selectedPropertyId,
    hoveredPropertyId,
    compSelectionTableVariant,
  ]);

  useEffect(() => {
    selectedStatusesRef.current = selectedStatuses;
  }, [selectedStatuses]);

  fitPinsRef.current = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const visiblePins = pinsRef.current.filter((pin) =>
      selectedStatusesRef.current.includes(pin.status),
    );
    fitBounds(map, [
      ...visiblePins.map((pin) => ({ latitude: pin.lat, longitude: pin.lng })),
      { latitude: subjectLat, longitude: subjectLng },
    ]);
  };

  useMap({
    mapContainerRef,
    mapRef,
    center: { lat: subjectLat, lng: subjectLng },
    zoom: DEFAULT_ZOOM,
    subject: { lat: subjectLat, lng: subjectLng },
    pins,
    selectedStatuses,
    enableStatusFiltering: !isFilterPanelOpen,
    statusFilterVariant: compSelectionTableVariant === 'rental' ? 'rental' : 'sales',
    enablePolygonDraw: true,
    showPolygonDrawControl: false,
    showTrashControl: false,
    controlsPosition: 'bottom-right',
    showCompass: false,
    fitPinsRef,
    controlsBottomOffsetPx: MAP_VIEWS_OVERLAY_BOTTOM_OFFSET_PX,
    callbacks: {
      onMapLoad: () => setMapLoaded(true),
      onStyleLoad: () => {
        setMapLoaded(true);
        const map = mapRef.current?.getMap();
        if (!map) return;
        if (map.getSource(MAP_SOURCES.PINS)) {
          if (map.getLayer(MAP_LAYERS.PINS_HIT)) {
            map.removeLayer(MAP_LAYERS.PINS_HIT);
          }
          if (map.getLayer(MAP_LAYERS.PINS)) {
            map.removeLayer(MAP_LAYERS.PINS);
          }
          if (map.getLayer(MAP_LAYERS.PINS_HOVER)) {
            map.removeLayer(MAP_LAYERS.PINS_HOVER);
          }
          map.removeSource(MAP_SOURCES.PINS);
        }
        map.addSource(MAP_SOURCES.PINS, propertyPinsToGeoJSON(pinsRef.current));
        map.addLayer(MAP_LAYER_DEFINITIONS.PINS_HOVER);
        map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
        map.addLayer(MAP_LAYER_DEFINITIONS.PINS_HIT);
      },
      onPinFilterChange: (statuses: PinStatusFilter[]) => {
        setSelectedStatuses(statuses);
      },
      onPinSourceLoad: () => {},
      onPinClick: (feature: Feature | undefined) => {
        if (!compSelectionTableRef?.current || !feature?.properties?.id) return;
        const pinId = feature.properties.id;

        const row = compSelectionTableRef.current.getSortedRowModel().rowsById[pinId];
        if (!row) return;

        setFocusedRow(row.id);
      },
      onPinHoverChange: onHoverPropertyChange,
      onPolygonDrawCreate: onPolygonCreateAndUpdate,
      onPolygonDrawUpdate: onPolygonCreateAndUpdate,
      onPolygonDrawDelete: () => {
        const mapBox = mapRef.current;
        const map = mapBox?.getMap();
        const draw = mapBox?.getDraw();
        if (map && draw && map.hasControl(draw as any)) {
          draw.changeMode('draw_polygon');
        }
        onPolygonDrawDelete?.();
      },
    },
  });

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!mapLoaded || !map) return;

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      map.easeTo({ center: [lng, lat], zoom: map.getZoom(), duration: 350 });
    }
  }, [lat, lng, mapLoaded, selectedPropertyId]);

  const activeGeoSearch = geoSearch ?? DEFAULT_GEO_SEARCH;

  useRadiusCircle({
    mapRef,
    subjectLat,
    subjectLng,
    geoSearch: activeGeoSearch,
    mapLoaded,
  });

  useProximityRequirementCircle({
    mapRef,
    subjectLat,
    subjectLng,
    proximityRequirementMiles,
    mapLoaded,
  });

  useMunicipalityBoundaryOverlay({
    mapRef,
    mapLoaded,
    enabled: Boolean(locationBoundaryEnabled),
    locationBoundary,
  });

  useRedrawPolygon({
    mapRef,
    allowPolygonDraw: true,
    geoSearch: activeGeoSearch,
    mapLoaded,
    subjectLat,
    subjectLng,
  });

  useEffect(() => {
    const url = mapType === 'satellite' ? MAP_STYLES.SATELLITE.url : MAP_STYLES.STREET.url;
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;
    map?.setStyle(url);
  }, [mapType]);

  const clearPolygon = () => {
    mapRef.current?.getMap().fire('draw.delete');
  };

  return { mapContainerRef, clearPolygon };
};
